/**
 * Onboarding step 3 — Quality standards: definition of good, definition
 * of bad, and hard guardrails. These become `rules` facts in the Brain
 * on activation — the operator guardrails every agent reads.
 */

import type { OnboardingDraft } from '../../../lib/onboarding';

interface Props {
  draft: OnboardingDraft;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

const FIELDS = [
  {
    key: 'good' as const,
    id: 'onb-quality-good',
    label: 'Definition of good',
    placeholder: 'e.g. Every touch is specific to the person — references their world, not ours.',
  },
  {
    key: 'bad' as const,
    id: 'onb-quality-bad',
    label: 'Definition of bad',
    placeholder: 'e.g. Generic spray-and-pray outreach; claims we can\u2019t back with evidence.',
  },
  {
    key: 'guardrails' as const,
    id: 'onb-quality-guardrails',
    label: 'Hard guardrails',
    placeholder: 'e.g. Never contact churned customers without approval. No discounts over 15%.',
  },
];

export function StepQuality({ draft, onPatch }: Props) {
  const set = (key: 'good' | 'bad' | 'guardrails', value: string) =>
    onPatch({ quality: { ...draft.quality, [key]: value } });

  return (
    <div className="onb-step">
      <h3 className="t-section-title onb-step__title">Quality standards</h3>
      <p className="t-meta onb-step__blurb">
        Draw the lines once — every agent proposal is judged against them.
      </p>
      {FIELDS.map((f) => (
        <div key={f.key}>
          <label className="t-eyebrow onb-label" htmlFor={f.id}>
            {f.label}
          </label>
          <textarea
            id={f.id}
            className="glass-input onb-textarea"
            rows={2}
            value={draft.quality[f.key]}
            onChange={(e) => set(f.key, e.target.value)}
            placeholder={f.placeholder}
          />
        </div>
      ))}
    </div>
  );
}
